import React, { useContext, useState, useEffect } from 'react';
import { cloneDeep } from 'lodash';
import { useNavigate, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
// import * as xlsx from 'xlsx';
import {
  Layout,
  Select,
  Segmented,
  Input,
  Row,
  Col,
  message,
  Upload,
  Button,
  Collapse,
  List,
  Progress,
  Avatar,
} from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import {
  UnorderedListOutlined,
  TableOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import { StoreContext } from '../store/index';
import {
  onCollectionChange,
  getShiftDateFromTimestamp,
  getLeagueDateFromTimestamp,
} from '../utils';
import { VIEW, STATE } from '../constants';
import {
  fetchShifts,
  fetchShiftsByUser,
  fetchShiftById,
  updateShift,
} from './api';
import Shift from './shift';
import './index.css';
import AddShift from './addShift';
import ShiftsTable from './shiftsTable';
import AddLeagueModal from '../leagues/addLeagueModal';
import { fetchLeagues } from '../leagues/api';
import League from '../leagues/League';

const Shifts = () => {
  const store = useContext(StoreContext);
  const {
    user: [user],
  } = store;

  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const { Search } = Input;
  const { Option } = Select;
  const { Content } = Layout;
  const location = useLocation();
  const [shifts, setShifts] = useState([]);
  const [filteredShifts, setFilteredShifts] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [filteredLeagues, setFilteredLeagues] = useState([]);
  const [ownerUnplayedShifts, setOwnerUnplayedShifts] = useState([]);
  const [payments, setPayments] = useState();
  const [place, setPlace] = useState(null);
  const [field, setField] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [placeFilterOptions, setPlaceFilterOptions] = useState([]);
  const [placeFilter, setPlaceFilter] = useState([]);
  const [sportFilter, setSportFilter] = useState();
  const [stateFilter, setStateFilter] = useState(STATE.IN);
  const [isGameFilter, setIsGameFilter] = useState(true);
  const [showAddLeagueModal, setShowAddLeagueModal] = useState(false);
  const [view, setView] = useState();
  const [loading, setLoading] = useState(true);

  const [shiftsChanged, setShiftsChanged] = useState(false);
  const [leaguesChanged, setLeaguesChanged] = useState(false);

  const getFilterPlaceOptions = () => {
    const names = shifts.map(({ placeName }) => placeName);
    const setNames = [...new Set(names)];
    return setNames.map(name => <Option key={name}>{name}</Option>);
  };

  const filterLeaguesByState = data =>
    data.filter(({ isFinished, startDate }) => {
      if (stateFilter === STATE.NEW) {
        return !isFinished && startDate < new Date();
      }
      if (stateFilter === STATE.IN) {
        return !isFinished && startDate >= new Date();
      }
      if (stateFilter === STATE.OLD) {
        return isFinished;
      }
      return false;
    });
  const filterShiftsByState = data =>
    data.filter(({ isFinished, players, bookRequests, playRequests }) => {
      if (stateFilter === STATE.NEW) {
        return (
          (!isFinished &&
            bookRequests.find(({ userId }) => userId === user.uid)) ===
            undefined &&
          players.find(({ userId }) => userId === user.uid) === undefined &&
          playRequests.find(({ userId }) => userId === user.uid) === undefined
        );
      }
      if (stateFilter === STATE.IN) {
        return (
          !isFinished &&
          (bookRequests.find(({ userId }) => userId === user.uid) ||
            players.find(({ userId }) => userId === user.uid) ||
            playRequests.find(({ userId }) => userId === user.uid))
        );
      }
      if (stateFilter === STATE.OLD) {
        return isFinished;
      }
      return false;
    });

  const filterByPlace = data =>
    placeFilter.length > 0
      ? data.filter(({ placeName }) => placeFilter.includes(placeName))
      : data;

  const filterByOrganizerUnplayed = data =>
    data.filter(({ userId, isFinished }) => userId === user.uid && !isFinished);

  const applyShiftFilters = () => {
    const visibleShifts = shifts.filter(
      s =>
        s.isPublic ||
        (!s.isPublic && s.userId === user.uid) ||
        (!s.isPublic && s.players.find(({ userId }) => userId === user.uid)) ||
        (!s.isPublic &&
          s.playRequests.find(({ userId }) => userId === user.uid)),
    );
    if (location?.search === '') {
      setFilteredShifts(filterShiftsByState(filterByPlace(visibleShifts)));
    } else setFilteredShifts(visibleShifts);
  };

  const getFilterPlaceDefaultValue = () =>
    location?.state?.place?.name ? [location.state.place.name] : [];

  const searchById = id => {
    if (id !== '') {
      fetchShiftById(id, setLoading).then(data => {
        if (data?.length > 0) {
          setShowFilters(false);
          setView(VIEW.LIST);
          setShifts(data);
        } else {
          message.error(`${id} no existe`);
          navigate('/');
        }
      });
    }
  };

  useEffect(() => {
    onCollectionChange('shifts', setShiftsChanged);
    onCollectionChange('leagues', setLeaguesChanged);
    setView(VIEW.LIST);
  }, []);

  useEffect(() => {
    if (isGameFilter) {
      if (location?.state?.place) {
        setPlace(location.state.place);
        setField(location.state.field);
        setPlaceFilter(getFilterPlaceDefaultValue);
      } else setPlace(null);

      if (location?.search !== '') {
        const id = location.search.replace('?', '');
        searchById(id);
      } else if (user.isOwner) {
        fetchShiftsByUser(user.uid, setLoading).then(data => setShifts(data));
      } else {
        fetchShifts(setLoading).then(data => {
          setShifts(data);
        });
      }
    }
    fetchLeagues(setLoading).then(data => {
      setLeagues(data);
    });
  }, [shiftsChanged, leaguesChanged]);

  useEffect(() => {
    if (isGameFilter) {
      if (user.isOwner) {
        setPlaceFilterOptions(getFilterPlaceOptions());
      }
      applyShiftFilters();
      setOwnerUnplayedShifts(filterByOrganizerUnplayed(shifts));
    } else {
      setFilteredLeagues(filterLeaguesByState(leagues));
    }
  }, [shifts, leagues, placeFilter, sportFilter, stateFilter, isGameFilter]);

  const handleUploadTransactions = ({ file }) => {
    // const reader = new FileReader();
    // const rABS = !!reader.readAsBinaryString;
    // reader.onload = e => {
    //  const bstr = e.target.result;
    //  const wb = xlsx.read(bstr, { type: rABS ? 'binary' : 'array' });
    //  const wsname = wb.SheetNames[0];
    //  const ws = wb.Sheets[wsname];
    //  let data = xlsx.utils.sheet_to_json(ws, { header: 1 });
    //  data = data.map(item => ({
    //    description: item[3]?.toString() || '', // numero de columna de la descripcion
    //    amount: item[4] || 0, // columna de valor
    //  }));
    //  setPayments(data);
    // };
    // if (rABS) reader.readAsBinaryString(file.originFileObj);
    // else reader.readAsArrayBuffer(file.originFileObj);
  };

  const dummyRequest = ({ file, onSuccess }) => {
    setTimeout(() => {
      onSuccess('ok');
    }, 0);
  };

  const updatePlayersPayment = (data, id) => {
    updateShift(
      id,
      {
        players: data,
      },
      setLoading,
    );
  };

  const setPlayersPayment = (shiftPayments, players, id) => {
    const playersWithPayments = players?.map(p => ({
      ...p,
      payment:
        shiftPayments.find(data =>
          data.description.toLowerCase().includes(p.mail),
        )?.amount || 0,
    }));
    updatePlayersPayment(playersWithPayments, id);
  };

  useEffect(() => {
    // ownerUnplayedShifts?.forEach(s =>
    //   setPlayersPayment(
    //     payments && payments.filter(data =>
    //       data.description
    //         .toLowerCase()
    //         .includes(s.id.slice(0, 5).toString().toLowerCase()),
    //     ),
    //     s.players,
    //     s.id,
    //   ),
    // );
  }, [payments]);

  const renderShift = shift => (
    <List.Item style={{ padding: '0px', margin: '0px' }} key={shift.id}>
      <Collapse
        bordered={false}
        items={[
          {
            key: 'shifts',
            label: (
              <Col>
                <Row justify="space-between">
                  <h3>
                    {`${getShiftDateFromTimestamp(
                      shift.startDate,
                      user.languageSelected,
                    )} - ${shift.organizerName}`}
                  </h3>
                  <h3>
                    {`${shift.totalPlayers / 2} vs 
                ${shift.totalPlayers / 2}`}
                  </h3>
                </Row>
                <Row justify="space-between">
                  <h5>{shift.placeName}</h5>
                  <h5>
                    {`${shift.players.length}/${
                      shift.totalPlayers
                    } ${formatMessage({
                      id: 'shifts.confirmed',
                    })}`}
                  </h5>
                </Row>
                <Row>
                  <Progress
                    percent={(shift.players.length / shift.totalPlayers) * 100}
                    showInfo={false}
                  />
                </Row>
              </Col>
            ),
            children: <Shift key={shift.id} shift={shift} />,
          },
        ]}
      />
    </List.Item>
  );

  const renderLeagues = league => (
    <List.Item style={{ padding: '0px', margin: '0px' }} key={league.id}>
      <Collapse
        bordered={false}
        items={[
          {
            key: 'leagues',
            label: (
              <Col>
                <Row justify="space-between">
                  <h3>
                    {`${league.totalPlayers / 2} vs 
                ${league.totalPlayers / 2} - ${league.name}`}
                  </h3>
                  <h3>
                    {`${formatMessage({
                      id: 'leagues.title.date.start',
                    })}
                ${getLeagueDateFromTimestamp(
                  league.startDate,
                  user.languageSelected,
                )}`}
                  </h3>
                </Row>
                <Row justify="space-between">
                  <h5>{league?.placeName}</h5>
                </Row>
              </Col>
            ),
            children: <League key={league.id} league={league} />,
          },
        ]}
      />
    </List.Item>
  );

  return (
    <Layout>
      <Content style={{ padding: '8px' }}>
        {view === VIEW.LIST ? (
          <List
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.7)',
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
            header={
              showFilters && (
                <>
                  <Row>
                    <Segmented
                      className="width100"
                      style={{ marginTop: '-12px' }}
                      block
                      options={[
                        {
                          label: (
                            <div style={{ padding: 4 }}>
                              <div style={{ fontSize: '32px' }}>⚽</div>
                              <div>
                                {formatMessage({
                                  id: 'layout.games',
                                })}
                              </div>
                            </div>
                          ),
                          value: true,
                        },
                        {
                          label: (
                            <div style={{ padding: 4 }}>
                              <div style={{ fontSize: '32px' }}>🏆</div>
                              <div>
                                {formatMessage({
                                  id: 'layout.leagues',
                                })}
                              </div>
                            </div>
                          ),
                          value: false,
                        },
                      ]}
                      value={isGameFilter}
                      onChange={setIsGameFilter}
                    />
                  </Row>
                  <Row>
                    {user.isOwner && (
                      <Col>
                        <Select
                          mode="multiple"
                          style={{ width: '200px' }}
                          placeholder={formatMessage({
                            id: 'shifts.filter.place',
                            defaultMessage: 'Filter by Place',
                          })}
                          defaultValue={getFilterPlaceDefaultValue()}
                          onChange={setPlaceFilter}>
                          {placeFilterOptions}
                        </Select>
                      </Col>
                    )}
                    <Segmented
                      className="width100"
                      style={{ marginTop: '-4px' }}
                      block
                      options={[
                        {
                          label: isGameFilter
                            ? formatMessage({
                                id: 'shifts.filters.old',
                                defaultMessage: 'Old',
                              })
                            : formatMessage({
                                id: 'leagues.filters.old',
                                defaultMessage: 'Old',
                              }),
                          value: STATE.OLD,
                        },
                        {
                          label: isGameFilter
                            ? formatMessage({
                                id: 'shifts.filters.in',
                                defaultMessage: 'In',
                              })
                            : formatMessage({
                                id: 'leagues.filters.in',
                                defaultMessage: 'In',
                              }),
                          value: STATE.IN,
                        },
                        {
                          label: isGameFilter
                            ? formatMessage({
                                id: 'shifts.filters.new',
                                defaultMessage: 'New',
                              })
                            : formatMessage({
                                id: 'leagues.filters.new',
                                defaultMessage: 'New',
                              }),
                          value: STATE.NEW,
                        },
                      ]}
                      value={stateFilter}
                      onChange={setStateFilter}
                    />
                  </Row>
                  {user.isAdmin && false && (
                    <Row gutter={[16, 16]}>
                      <Col>
                        <Segmented
                          options={[
                            {
                              value: VIEW.GRID,
                              icon: <TableOutlined />,
                            },
                            {
                              value: VIEW.LIST,
                              icon: <UnorderedListOutlined />,
                            },
                          ]}
                          value={view}
                          onChange={setView}
                        />
                      </Col>
                      {ownerUnplayedShifts?.length > 0 && (
                        <Col>
                          <Upload
                            showUploadList={false}
                            maxCount={1}
                            onChange={handleUploadTransactions}
                            customRequest={dummyRequest}>
                            <Button loading={loading} icon={<UploadOutlined />}>
                              {formatMessage({
                                id: 'common.actions.verify.payments',
                              })}
                            </Button>
                          </Upload>
                        </Col>
                      )}
                    </Row>
                  )}
                </>
              )
            }
            key="filteredShifts"
            size="large"
            itemLayout="vertical"
            loading={loading}
            dataSource={
              isGameFilter ? cloneDeep(filteredShifts) : filteredLeagues
            }
            renderItem={item =>
              isGameFilter ? renderShift(item) : renderLeagues(item)
            }
          />
        ) : (
          <ShiftsTable shifts={filteredShifts} isLoading={loading} />
        )}
        {isGameFilter ? (
          <AddShift field={field} />
        ) : (
          <Button
            style={{ marginTop: '4px', marginBottom: '4px' }}
            block
            type="primary"
            loading={loading}
            onClick={() => setShowAddLeagueModal(true)}>
            {formatMessage({ id: 'leagues.actions.add' })}
          </Button>
        )}
        {showAddLeagueModal && (
          <AddLeagueModal
            visible={showAddLeagueModal}
            onClose={() => setShowAddLeagueModal(false)}
          />
        )}
      </Content>
    </Layout>
  );
};

export default Shifts;
